import { useNavigate } from "react-router-dom";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { format } from "date-fns";

import { neutral } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import { dateDifferenceText } from "components/suno-dialer/utils";
import { CRM_SEGMENTS } from "constants/patients";
import { isBlank } from "utils/common";

export default function InfoBlock() {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    incomingCallDetails,
    isPastAppointment,
    appointment,
    patient,
    isLoading
  } = useSunoDialer();
  const { callerId } = incomingCallDetails || {};

  const patientAddedText = patient?.created_at_display
    ? dateDifferenceText(patient?.created_at_display)
    : "";

  if (isLoading)
    return (
      <Box sx={{ px: 2, pt: 2 }}>
        <Stack
          spacing={1.5}
          sx={{
            color: "white",
            background: "hsla(273, 21%, 54%, 0.7)",
            px: 1.5,
            pt: 1.5,
            pb: 1.5,
            borderRadius: "8px",
            boxShadow: theme.shadows[3]
          }}
        >
          <CircularProgress sx={{ p: 0 }} />
        </Stack>
      </Box>
    );

  if (
    [
      patient?.crm_segment,
      patientAddedText,
      callerId,
      appointment?.startDate
    ].every(isBlank)
  )
    return null;

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Stack
        spacing={1.5}
        sx={{
          color: "white",
          background: "hsla(273, 21%, 54%, 0.7)",
          px: 1.5,
          pt: 1.5,
          pb: 1.5,
          borderRadius: "8px",
          boxShadow: theme.shadows[3]
        }}
      >
        {Boolean(patient?.crm_segment) && (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              background: "hsla(9, 0%, 100%, 0.75)",
              borderRadius: "8px",
              color: neutral[900],
              width: "fit-content"
            }}
          >
            <Typography sx={{ fontSize: "17px", lineHeight: 1.3 }}>
              {CRM_SEGMENTS.getFromCode(patient?.crm_segment)}
            </Typography>
          </Box>
        )}

        {Boolean(patientAddedText) && (
          <Typography sx={{ fontSize: "15px", lineHeight: 1.1 }}>
            Patient added {patientAddedText}
          </Typography>
        )}

        {Boolean(callerId && appointment?.startDate) && (
          <Stack spacing={0.5}>
            <Typography
              sx={{ fontSize: "15px", fontWeight: 700, lineHeight: 1.3 }}
            >
              {isPastAppointment ? "Past" : "Next"} Appointment:
            </Typography>
            <Link
              href={`/patients/${callerId}/appointments/${
                appointment?.id || ""
              }`}
              sx={{
                width: "fit-content",
                color: "white",
                textDecorationColor: neutral[600],
                "&:hover": {
                  color: "white"
                }
              }}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                navigate(`/patients/${callerId}/appointments`);
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: "15px", lineHeight: 1.3 }}
              >
                <CalendarMonthIcon
                  sx={{ fontSize: "15px", transform: "translateY(1px)" }}
                />{" "}
                {format(
                  new Date(appointment?.startDate || ""),
                  "EEE MMM dd, yyyy hh:mm aaa"
                )}
              </Typography>
            </Link>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
